import { useRef, useEffect, useState, Fragment, memo, useMemo, useContext } from 'react';
import { values, groupBy, sortBy } from 'lodash'
import classnames from 'classnames';
import usePlatform from '../../hooks/usePlatform';
import { HoverProvider, HoverContext } from './hover-provider'
import IconAiArrow from '../../components/IconWrapper/arrow-ai';
import PureVideoModal from '../../pages/ai/pure-video-modal';
import Section from '../../pages/ai/section';
import { getAIStory } from '../../pages/ai/services';
import './story.scss'

interface OrganizationRowProps {
  org: {
    name: string;
    employeeBOS: {
      id: number;
      position: string;
      name: string;
      avatar: string;
      aiAvatar: string;
      aiPosition: string;
    }[];
  };
}
interface OrganizationProps {
  user: {
    id: number;
    position: string;
    name: string;
    avatar: string;
    aiAvatar: string;
    aiPosition: string;
  };
}
const Organization = ({ user }: OrganizationProps) => {
  const { hoveredId, setHoveredId } = useContext(HoverContext);
  const isHovered = hoveredId === user.id;
  return (
    <div
      className={classnames("org-user-wrapper", { "hover": isHovered })}
      onMouseEnter={() => setHoveredId(user.id)}
      onMouseLeave={() => setHoveredId(null)}
    >
      <div className="org-user" key={user.name}>
        <img className="org-user-image" src={user.avatar} alt={user.name} />
        <div> {user.position} · {user.name}</div>
      </div>
      <div className="inline-block">
        <div className="org-user small" key={user.name}>
          <img className="org-user-image small" src={user.aiAvatar} alt={user.name} />
          <div> {user.aiPosition} AI顾问</div>
        </div>
      </div>
    </div>
  )
}
const OrganizationRow = memo(({ org }: OrganizationRowProps) => {
  const showEmpty = org.employeeBOS.length > 1 && (org.employeeBOS.length % 2 === 1);
  const list = sortBy(org.employeeBOS, 'order')
  return <div className="org-wrapper">
    <div className="org-name"> {org.name} </div>
    <div className="org-users">
      {list.map((user, index) => {
        if (list.length > 2 && index > 1&& index % 2 === 0 && list[index + 1]) { // 交换位置
          const temp = list[index + 1]
          list[index + 1] = user;
          list[index] = temp
        }
        const finalUser = list[index];
        return <Organization key={finalUser.id} user={finalUser}/>
      })}
      {showEmpty && <div className="org-user-wrapper"></div>}
    </div>
  </div>
})

interface CardsRowProps {
  isPc: boolean;
  cards: {
    id: number;
    name: string;
    storyLineSceneRelationBOS: { id: number; name: string; scene: { pcLink: string }, sceneName: string }[];
    employee: { id: number; position: string, departmentLevel: number };
    order: number;
  }[];
  isLeftDot: boolean;
}

interface DotProps {
  position: string;
  transparent?: boolean;
  order: number
  isPc: boolean;
}

// 透明的Dot连接上一个order的非透明的Dot
const drawLine = (order: number, position: string, isPc: boolean) => {
  // 获取容器
  const container = document.querySelector('.story-content-right');
  if (!container) return;

  // 查找或创建SVG容器
  let svg = document.getElementById('story-lines-svg') as any;
  if (!svg) {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('id', 'story-lines-svg');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    svg.style.pointerEvents = 'none';
    svg.style.zIndex = '0';
    container.appendChild(svg);
  }

  // 清除之前可能存在的这条线
  const existingPath = document.getElementById(`line-path-${order}`);
  if (existingPath) {
    existingPath.remove();
  }

  // 获取当前透明的dot和上一个非透明的dot
  const currentDot = document.getElementById(`dot-transparent-${order}`);
  const prevDot = document.getElementById(`dot-normal-${order - 1}`);

  // 确保元素存在
  if (!currentDot || !prevDot) return;

  // 获取元素位置
  const currentRect = currentDot.getBoundingClientRect();
  const prevRect = prevDot.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // 计算相对于容器的坐标
  const currentX = currentRect.left - containerRect.left + currentRect.width / 2;
  const currentY = currentRect.top - containerRect.top + currentRect.height / 2;
  const prevX = prevRect.left - containerRect.left + prevRect.width / 2;
  const prevY = prevRect.top - containerRect.top + prevRect.height / 2;

  // 设置圆角半径
  const radius = isPc ? 32 : 8;

  // 创建路径元素
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('id', `line-path-${order}`);
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'white');
  path.setAttribute('stroke-width', '2');

  let pathData = '';

  // 判断是直线连接还是需要圆角连接
  if (Math.abs(currentY - prevY) < 1) {
    // 同一水平线，直接连接
    path.setAttribute('stroke-dasharray', '2,2');
    pathData = `M ${prevX} ${prevY} L ${currentX} ${currentY}`;
  } else {
    path.setAttribute('stroke-dasharray', '4,4');
    // 垂直方向有差异，绘制对称的连接线（类似圆角矩形的竖向一半切割）
    if (position === 'right') {
      // 从右边出发的对称连接：先向右弯曲，然后直线，最后向左弯曲
      pathData = `M ${prevX} ${prevY}
                  Q ${prevX + radius} ${prevY}, ${prevX + radius} ${prevY + radius}
                  L ${prevX + radius} ${currentY - radius}
                  Q ${prevX + radius} ${currentY}, ${currentX} ${currentY}`;
    } else {
      // 从左边出发的对称连接：先向左弯曲，然后直线，最后向右弯曲
      pathData = `M ${prevX} ${prevY}
                  Q ${prevX - radius} ${prevY}, ${prevX - radius} ${prevY + radius}
                  L ${prevX - radius} ${currentY - radius}
                  Q ${prevX - radius} ${currentY}, ${currentX} ${currentY}`;
    }
  }

  path.setAttribute('d', pathData);
  svg.appendChild(path);
}
const Dot = ({ order, position, transparent = false, isPc }: DotProps) => {
  useEffect(() => {
    if (!transparent || order === 0) {
      return
    }
    drawLine(order, position, isPc);
    const handleResize = () => {
      drawLine(order, position, isPc);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [order, position, transparent, isPc])
  return <div id={`dot-${transparent ? 'transparent' : 'normal'}-${order}`} className={classnames("dot-wrapper", position, { transparent })}>
    <div className="dot"></div>
  </div>
}

interface CardItemProps {
  card: any;
  isPc: boolean;
  samePosition: boolean;
}
const CardItem = memo(({ card, isPc, samePosition }: CardItemProps) => {
  const { hoveredId, setHoveredId } = useContext(HoverContext);
  const [videoSrc, setVideoSrc] = useState(''); // 视频源
  const isHovered = hoveredId === card.employee.id;

  const closeVideoModal = () => {
    setVideoSrc('');
  }

  const onOpenVideoModal = (item: any) => {
    if (!item.scene?.pcResource) {
      return
    }
    setVideoSrc(item.scene?.pcResource)
  }
  return (
    <div
      className={classnames("card-item", { "hover": isHovered })}
      onMouseEnter={() => setHoveredId(card.employee.id)}
      onMouseLeave={() => setHoveredId(null)}
    >
      {!isPc && <div className="card-user">{`${card.employee.position} & AI顾问`}</div>}
      {!card.isLast && <Dot isPc={isPc} position={samePosition ? 'left' : 'right'} order={card.order}/>}
      <Dot isPc={isPc} position={samePosition ? 'right' : 'left'} transparent order={card.order}/>
      <div className="card-item-header">
        <div className="card-item-title">{card.name}</div>
        <div className="card-item-sort">{`O${card.order}`}</div>
      </div>
      <div className="card-item-list">
        {card.storyLineSceneRelationBOS?.map((item: any) => (
          item.scene?.pcLink  ? <a className="card-item-list-item" key={item.id} href={item.scene?.pcLink} target= "_blank">
            <span>{`${item.sceneName || ''}`}</span>
            <IconAiArrow />
          </a>:
           <span key={item.id} className="card-item-list-item" onClick={() => onOpenVideoModal(item)}>
              <span>{`${item.sceneName || ''}`}</span>
              {item.scene?.pcResource && <IconAiArrow />}
           </span>
        ))}
      </div>
      <PureVideoModal
        videoModalVisible={!!videoSrc}
        videoSrc={videoSrc}
        closeVideoModal={closeVideoModal}
      />
    </div>
  )
})

const CardsRow = memo(({ cards, isPc, isLeftDot }: CardsRowProps) => {
  const showEmpty = cards.length > 1 && (cards.length % 2 === 1);
  const list = sortBy(cards, 'order')
  return <div className="cards-wrapper">
    {list.map((card, index) => {
      const samePosition =  parseInt((index / 2).toString()) % 2 === 0 ? isLeftDot : !isLeftDot;
      if (list.length > 2 && index > 1 && index % 2 === 0 && list[index + 1]) { // 交换位置
        const temp = list[index + 1]
        list[index + 1] = card;
        list[index] = temp
      }
      const finalCard = list[index];
      return <CardItem key={finalCard.id} card={finalCard} isPc={isPc} samePosition={samePosition} />})}
    {showEmpty && <div className="card-item"></div>}
  </div>
})

interface CardsRowWrapperProps {
  story: any;
  isPc: boolean;
}
const CardsRowWrapper = ({ story, isPc }: CardsRowWrapperProps) => {
  const beginDotLeftRef = useRef<any>({ 0: true });
  const storyLineBOS = useMemo(() => story.storyLineBOS.map((s: any, i: number) => ({ ...s, order:i + 1, isLast: i === story.storyLineBOS.length - 1 })), [story.storyLineBOS])
  const chunkCards = values(groupBy(storyLineBOS, 'employee.departmentLevel')) || []

  return <>
    {chunkCards.map((items, i) => {
      if (beginDotLeftRef.current[i] === undefined) {
        const lastOne = beginDotLeftRef.current[i-1]
        beginDotLeftRef.current[i] = parseInt((items.length / 2).toString()) % 2 === 0 ? !lastOne : lastOne;
      }
      const departmentLevelBO = story.departmentLevelBOS?.find((item: any) => item.id === items[0].employee.departmentLevel)
      return <Fragment key={items[0].employee.departmentLevel}>
        {!isPc && <div className="story-content-title">{`······ ${departmentLevelBO?.name || ''} ······`}</div>}
        <CardsRow isLeftDot={beginDotLeftRef.current[i]} cards={items} isPc={isPc}/>
      </Fragment>
    })}
  </>
}

export default ({ className }: { className?: string }) => {
  const { isPc } = usePlatform() || {};
  const [story, setStory] = useState<any>({});
  useEffect(() => {
    getAIStory().then((res: any) => {
      setStory(res || {})
    })
  }, [])

  if (!story.id) {
    return null;
  }
  return <HoverProvider>
    <Section title={story.title} description={story.description} className={className}>
      <div className="story-content">
        {isPc && <div className="story-content-left">
          {story.departmentLevelBOS?.map((org: any) => (
            <OrganizationRow key={org.id} org={org} />
          ))}
        </div>}
        <div className="story-content-right">
          <CardsRowWrapper story={story} isPc={isPc} />
        </div>
      </div>
    </Section>
  </HoverProvider>
}
